<template>
  <div>
    <VTextField
      ref="field"
      v-model="inputValue"
      v-bind="$attrs"
      :label="label"
      :variant="variant"
      :rounded="0"
      :hide-details="hideErrors || (hideDetailsWithoutError && !(errorMessage || (manualErrors ?? []).length > 0))"
      :error-messages="hideErrors ? undefined : errorMessages(errorMessage)"
      :style="textFieldStyle"
      :class="{ 'custom-size': !!size }"
      @blur="(e: FocusEvent) => handleBlur(e, true)"
    >
      <template v-for="(_, slot) in $slots" #[slot]="slotProps">
        <slot :name="slot" v-bind="slotProps ?? {}" />
      </template>
    </VTextField>
  </div>
</template>

<script setup lang="ts">
import type { RuleExpression} from 'vee-validate';
import { useFieldHtmlAttribute, useFieldLabel, useRevalidateOnValueUpdateAfterBlur } from "~/composables/fields";

interface Props {
 label?: string
 name: string
 variant?: 'filled' | 'outlined' | 'plain' | 'underlined' | 'solo' | 'solo-filled' | 'solo-inverted'
 size?: number
 rules?: RuleExpression<unknown>
 manualErrors?: string[]
 hideDetailsWithoutError?: boolean
 hideErrors?: boolean
 lengthLimitCounter?: number
 htmlAttributes?: { [key: string]: string }
 immediate?: boolean
 trim?: boolean
 dataTestid?: string | false | null
}
const props = withDefaults(defineProps<Props>(), {
  variant: 'outlined',
  label: undefined,
  lengthLimitCounter: undefined,
  manualErrors: () => [],
  hideDetailsWithoutError: false,
  size: undefined,
  rules: undefined,
  hideErrors: false,
  htmlAttributes: undefined,
  immediate: false,
  trim: true,
  dataTestid: undefined
})

const field = useTemplateRef('field')

watchEffect(() => {
  Object.entries(props.htmlAttributes ?? {}).forEach(([key, value]) => {
    useFieldHtmlAttribute({field: field, attribute: key, value: value, element: 'input'})
  })
})

// data-testid has to land on the native <input>, once: Playwright fills and
// clears getByTestId(...) and waits on input[data-testid=...], as it did on
// nuxt2. Vuetify 4's filterInputAttrs sends every data-* attr to the .v-input
// root, never the <input>, and inherited attrs put a second copy on the wrapper
// <div>. Declaring it as a prop keeps it out of $attrs, so neither gets a copy;
// it is set on the input after render instead. AB#65247
watchPostEffect(() => {
  const input = field.value?.$el?.querySelector('input')
  const testId = props.dataTestid
  if (!input) return
  if (testId == null || testId === false) {
    input.removeAttribute('data-testid')
  } else {
    input.setAttribute('data-testid', testId)
  }
})

const model = defineModel<unknown>()

const name = toRef(props, 'name');

// v-model is synced by hand (not syncVModel) so the emitted value is trimmed
// while the field keeps what the user typed, as nuxt2 emitted String(v).trim():
// trailing autofill spaces otherwise reach the email, sign-in and address
// payloads. Same approach as TextareaWithValidation. AB#64455
// Pass :trim="false" for a password: the rules validate what was typed, so a
// trimmed value would be submitted that the rules never saw.
const { value: inputValue, errorMessage, handleBlur, validate } = useField(name, props.rules || '', {
  // Every field wrapper resolves the message name the same way — `fieldname.<name>`
  // when a translation exists, otherwise the label prop. AB#63279
  label: useFieldLabel(name, () => props.label),
  initialValue: model.value,
  syncVModel: false,
  validateOnValueUpdate: false,
  controlled: true
});

const toEmittedValue = (value: unknown) => props.trim && value != null ? String(value).trim() : value
watch(inputValue, (value) => {
  model.value = toEmittedValue(value)
})
watch(model, (value) => {
  if (value !== toEmittedValue(inputValue.value)) {
    inputValue.value = value
  }
})

useRevalidateOnValueUpdateAfterBlur({ value: inputValue, errorMessage, validate })

// Imperative validation for parents that trigger validation outside a form
// submit (e.g. LinkBuilder's create-link flow). Matches CheckboxWithValidation.
// focus() is what AuthForm calls on the email field when the email already
// exists; without it that call threw a TypeError. AB#64455
const focus = async () => {
  await nextTick()
  field.value?.focus()
}

defineExpose({ focus, validate })

// const errorMessages = computed( () =>  errorMessage.value ?  [errorMessage, ...props.manualErrors] : props.manualErrors)
const errorMessages = (errorMessage?: string) =>  errorMessage ?  [errorMessage, ...(props.manualErrors ?? [])] : (props.manualErrors ?? [])
const textFieldStyle = computed(() => {
  if (!props.size) return {}
  return ({
    '--custom-height': `${props.size}px`,
  });
})
</script>
<style lang="scss" scoped>
.limit-reached {
  color: red;
}

.custom-size {
  :deep(.v-field__field) {
    height: var(--custom-height);
    input {
      // todo release revisit math for proper centering on different sizes
      padding-top:  calc(var(--custom-height) - 40px);
    }
  }
}

</style>
